import { autoinject, bindable, bindingMode, computedFrom } from "aurelia-framework";
import { validateTrigger, ValidationController, ValidationControllerFactory, ValidationRules } from "aurelia-validation";
import { appLogger } from "../../infrastructure/logging";
import { ChallengeService } from "../../services/challenge-service";
import { localGlobal } from "../../infrastructure/infrastructure";
import { IdentityClientHttpError, IRedirectErrorContainer } from "../../infrastructure/identity-client";

@autoinject
export class ConsumerLogin {
    @bindable({ defaultBindingMode: bindingMode.toView })
    public returnUrl: string;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public finalize: () => Promise<void>;
    private _challengeService: ChallengeService;
    public email: string;
    public password: string;

    public controller: ValidationController;
    public isLoading = false;
    public hasError = false;

    @computedFrom("returnUrl")
    public get encodedReturnUrl(): string {
        return encodeURIComponent(this.returnUrl);
    }

    public constructor(challengeService: ChallengeService, controllerFactory: ValidationControllerFactory) {
        this._challengeService = challengeService;
        this.controller = controllerFactory.createForCurrentScope();
        this.controller.validateTrigger = validateTrigger.changeOrBlur;
    }

    public executeRecaptcha() {
        localGlobal.grecaptcha.enterprise.ready(() => {
          localGlobal.grecaptcha.enterprise.execute(localGlobal._recaptchaSiteKey, { action: 'LOGIN' })
          .then((token: string) => {
            this.login(token);
          });
        });
      }

    public emailEntered(){
        this.email = this.email.trim();
    }

    public async login(gRecaptchaResponse: string) {
        this.hasError = false;
        let results = await this.controller.validate();
        if (!results.valid) {
            return;
        }
        try {
            this.isLoading = true;
            let result = await this._challengeService.challengeEndUserCredential(
                this.email,
                this.password,
                gRecaptchaResponse
            );
            let success = result != null;
            this.hasError = !success;
            if (this.hasError) {
                this.isLoading = false;
                return;
            }
            let identityResult = await this._challengeService.identityChallenge();
            if (this.returnUrl == null || identityResult == null) {
                this.hasError = true;
                this.isLoading = false;
                return;
            }
            window.location.replace(this.returnUrl);
        } catch (error) {
            if(error as IdentityClientHttpError) {
                var identityError = (error as IdentityClientHttpError);
                if(identityError.status === 403) {
                    var errorResponse = identityError.response as unknown as IRedirectErrorContainer;
                    window.location.assign(errorResponse.redirectUrl);
                    return;
                }
            }
            appLogger.error("An error occurred while trying to submit the sign-in form.", error);
            this.hasError = true;
        } finally {
            this.isLoading = false;
        }
    }
}

ValidationRules
    .ensure((x: ConsumerLogin) => x.password).required().minLength(12).withMessage("Your password must be at least 12 alphanumeric characters and include at least one uppercase letter, one lowercase letter and one number.").maxLength(256)
    .ensure((x: ConsumerLogin) => x.email).required().email()
    .on(ConsumerLogin);